/**
 * The subset of table-store editing state that signals an in-progress
 * (or dangling) cell edit. Kept structural so the store instance satisfies it.
 */
export type CellEditSnapshot = {
  isCellBeingEdited: boolean;
  editingCellId: string | null;
  draftCellValue: string | null;
  /** PSTUDIO-1433: the Expanded Cell View panel's open edit, if any. */
  panelEditingRowId?: string | null;
  panelEditingColumnId?: string | null;
};

/**
 * PSTUDIO-3813: true when a cell edit is live OR a draft/target lingers.
 *
 * Undo/redo must tear such an edit down before applying a history command,
 * otherwise the uncommitted draft is committed on the next blur/click/reveal
 * and clobbers the revert (e.g. `Test 13` -> `T`). Defensive on purpose: any one
 * of the three signals is enough, so a field desync (a dangling draft whose
 * `isCellBeingEdited` flag was already cleared — reproduced live as case B) is
 * still caught. An empty-string draft is a real, committable value -> active.
 *
 * PSTUDIO-1433 adds the panel's registration as a fourth signal. The Expanded
 * Cell View sets NONE of the first three while its commit is suppressed (an
 * in-panel click deliberately clears `isCellBeingEdited` and the panel never
 * sets the editing ids), so an undo landing in that window did not tear the
 * edit down — and the panel's later flush wrote the pre-undo draft back over
 * it.
 */
export function hasActiveCellEdit(snapshot: CellEditSnapshot): boolean {
  return (
    snapshot.isCellBeingEdited ||
    snapshot.editingCellId != null ||
    snapshot.draftCellValue != null ||
    snapshot.panelEditingRowId != null ||
    snapshot.panelEditingColumnId != null
  );
}

/**
 * Whether ANY editor holds an open edit — the inline grid editor or the
 * Expanded Cell View panel.
 *
 * A named helper rather than an inline `||` at the call site: the cross-tab
 * reload guard is the one consumer, and an inline expression there could only
 * be tested by re-typing it in the test file — which is what let a mutation
 * (dropping the panel half) leave the whole suite green (PSTUDIO-1433).
 */
export function isAnyCellEditOpen(snapshot: {
  isCellBeingEdited: boolean;
  panelEditingRowId?: string | null;
}): boolean {
  return (
    snapshot.isCellBeingEdited || (snapshot.panelEditingRowId ?? null) !== null
  );
}
